import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Form, Spinner, Alert, Row, Col, Card } from 'react-bootstrap';
import { fetchLocationTracking, fetchEmployees, fetchDesignations, fetchPorts } from './services/api';
import { useAuth } from './AuthContext';
import moment from 'moment-timezone';
import * as XLSX from 'xlsx';

interface Designation {
  id: number;
  name: string;
  remote_checkin: boolean;
  user_type: string;
}

interface Port {
  id: number;
  name: string;
  location: string;
}

interface Employee {
  id: number;
  name: string;
  designation?: Designation;
  port: number;
  is_active?: boolean;
}

interface LocationData {
  timestamp: string;
  latitude: number;
  longitude: number;
}

// Helper to format date as yyyy-MM-dd HH:mm:ss
function formatDate(date: string, endOfDay = false) {
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const time = endOfDay ? '23:59:59' : '00:00:00';
  return `${yyyy}-${mm}-${dd} ${time}`;
}

const LocationTrackingReport: React.FC = () => {
  const { token } = useAuth();
  const [employeeId, setEmployeeId] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [designationId, setDesignationId] = useState('');
  const [ports, setPorts] = useState<Port[]>([]);
  const [portId, setPortId] = useState('');
  const [data, setData] = useState<LocationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationNames, setLocationNames] = useState<{ [key: string]: string }>({});
  const locationCache = useRef<{ [key: string]: string }>({});

  useEffect(() => {
    fetchDesignations()
      .then(response => {
        const designations = Array.isArray(response.data) ? response.data : [];
        setDesignations(designations.filter(d => d.remote_checkin));
      })
      .catch(() => setDesignations([]));

    fetchPorts()
      .then(response => {
        const ports = Array.isArray(response.data.data) ? response.data.data : [];
        setPorts(ports);
      })
      .catch(() => setPorts([]));

    if (token && token.designation?.user_type === 'ADMIN' && token.port) {
      setPortId(String(token.port));
    }
    if (token && token.designation?.user_type === 'ADMIN') {
      setEmployeeId(String(token.id));
    }
  }, [token]);

  useEffect(() => {
    fetchEmployees()
      .then(response => {
        const employees = Array.isArray(response.data) ? response.data : [];
        let filtered = employees.filter((emp: Employee) => emp.designation?.remote_checkin && emp.is_active !== false);
        if (designationId) {
          filtered = filtered.filter((emp: Employee) => String(emp.designation?.id) === designationId);
        }
        if (portId) {
          filtered = filtered.filter((emp: Employee) => String(emp.port) === portId);
        }
        setEmployees(filtered);
      })
      .catch(() => setEmployees([]));
  }, [designationId, portId]);

  const getLocationName = async (lat: number, lon: number) => {
    const key = `${lat},${lon}`;
    if (locationCache.current[key]) return locationCache.current[key];
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`);
      const json = await res.json();
      const name = json.display_name || `${lat}, ${lon}`;
      locationCache.current[key] = name;
      setLocationNames(prev => ({ ...prev, [key]: name }));
      return name;
    } catch {
      locationCache.current[key] = `${lat}, ${lon}`;
      setLocationNames(prev => ({ ...prev, [key]: `${lat}, ${lon}` }));
      return `${lat}, ${lon}`;
    }
  };

  useEffect(() => {
    if (!data || data.length === 0) return;
    data.forEach(row => {
      const key = `${row.latitude},${row.longitude}`;
      if (!locationCache.current[key]) {
        getLocationName(row.latitude, row.longitude);
      }
    });
    // eslint-disable-next-line
  }, [data]);

  const handleFetch = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchLocationTracking(
        employeeId,
        formatDate(startDate, false),
        formatDate(endDate, true)
      );
      setData(Array.isArray(result) ? result : []);
    } catch (e) {
      console.error('Error fetching location data:', e);
      setError('Failed to fetch location tracking data');
    }
    setLoading(false);
  };

  const handleExportToExcel = async () => {
    if (data.length === 0) {
      alert('No data to export');
      return;
    }

    // Find selected employee name
    const selectedEmployee = employees.find(emp => String(emp.id) === employeeId);
    const employeeName = selectedEmployee?.name || 'Unknown Employee';

    // Prepare data for Excel export - wait for all location names to be resolved
    const exportData = await Promise.all(
      data.map(async (item: LocationData, index: number) => {
        const key = `${item.latitude},${item.longitude}`;
        let locationName = locationNames[key];

        // If location name is not available, try to get it
        if (!locationName || locationName === 'Loading...') {
          locationName = await getLocationName(item.latitude, item.longitude);
        }

        return {
          'S.No': index + 1,
          'Employee Name': employeeName,
          'Timestamp': item.timestamp ? moment.utc(item.timestamp).format('YYYY-MM-DD hh:mm A') : '',
          'Latitude': item.latitude,
          'Longitude': item.longitude,
          'Location Name': locationName,
          'Google Maps URL': `https://www.google.com/maps?q=${item.latitude},${item.longitude}`
        };
      })
    );

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    const columnWidths = [
      { wch: 8 },   // S.No
      { wch: 25 },  // Employee Name
      { wch: 20 },  // Timestamp
      { wch: 12 },  // Latitude
      { wch: 12 },  // Longitude
      { wch: 50 },  // Location Name
      { wch: 40 }   // Google Maps URL
    ];
    worksheet['!cols'] = columnWidths;

    // Make Google Maps URLs clickable hyperlinks
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    for (let row = 1; row <= range.e.r; row++) { // Start from row 1 to skip header
      // Google Maps URL (column G = 6)
      const mapsCell = XLSX.utils.encode_cell({ r: row, c: 6 });
      if (worksheet[mapsCell] && worksheet[mapsCell].v) {
        worksheet[mapsCell] = {
          t: 's',
          v: worksheet[mapsCell].v,
          l: { Target: worksheet[mapsCell].v, Tooltip: 'Click to view location on Google Maps' }
        };
      }
    }

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Geofence Map users');

    // Generate filename with current date and filters
    const today = moment().format('YYYY-MM-DD');
    let filename = `Location_Tracking_Report_${today}`;

    if (employeeName && employeeName !== 'Unknown Employee') {
      filename += `_${employeeName.replace(/\s+/g, '_')}`;
    }

    if (startDate && endDate) {
      filename += `_${startDate}_to_${endDate}`;
    }

    filename += '.xlsx';

    // Download file
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div className="p-4 p-md-5">
      <h2>Geofence Map users Report</h2>
      <Card className="mb-3">
        <Card.Body>
          <Form>
            <Row className="align-items-end g-2">
              <Col md>
                <Form.Group controlId="port">
                  <Form.Label>Location</Form.Label>
                  <Form.Select value={portId} onChange={e => setPortId(e.target.value)} disabled={!!(token && token.designation?.user_type === 'ADMIN' && token.port)}>
                    <option value="">All Locations</option>
                    {ports.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md>
                <Form.Group controlId="designation">
                  <Form.Label>Designation</Form.Label>
                  <Form.Select value={designationId} onChange={e => setDesignationId(e.target.value)}>
                    <option value="">All Designations</option>
                    {designations.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md>
                <Form.Group controlId="employee">
                  <Form.Label>Employee</Form.Label>
                  <Form.Select value={employeeId} onChange={e => setEmployeeId(e.target.value)} >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name} ({emp.designation?.name})</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md>
                <Form.Group controlId="startDate">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md>
                <Form.Group controlId="endDate">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md="auto">
                <Button variant="primary" onClick={handleFetch} disabled={loading || !employeeId}>Fetch</Button>
              </Col>
              <Col md="auto">
                <Button
                  variant="primary"
                  onClick={handleExportToExcel}
                  disabled={data.length === 0 || loading}
                >
                  📊 Export to Excel
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
      {loading && (
        <div className="d-flex justify-content-center align-items-center my-3" style={{ minHeight: 40 }}>
          <Spinner animation="border" />
        </div>
      )}
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
        <Table striped bordered hover style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>#</th>
              <th>Timestamp</th>
              <th>Latitude</th>
              <th>Longitude</th>
              <th>Location Name</th>
              <th>Google Maps URL</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && !loading ? (
              <tr><td colSpan={6} className="text-center">No data</td></tr>
            ) : (
              data.map((row, idx) => {
                const key = `${row.latitude},${row.longitude}`;
                const googleMapsUrl = `https://www.google.com/maps?q=${row.latitude},${row.longitude}`;
                return (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{row.timestamp ? moment.utc(row.timestamp).format('YYYY-MM-DD hh:mm A') : ''}</td>
                    <td>{row.latitude}</td>
                    <td>{row.longitude}</td>
                    <td>{locationNames[key] || 'Loading...'}</td>
                    <td>
                      <a
                        href={googleMapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#156082',
                          textDecoration: 'none',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                        onMouseOver={(e) => (e.target as HTMLAnchorElement).style.textDecoration = 'underline'}
                        onMouseOut={(e) => (e.target as HTMLAnchorElement).style.textDecoration = 'none'}
                      >
                        🌍 View on Maps
                      </a>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default LocationTrackingReport; 